import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Phone, ArrowsClockwise, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Super-Admin · Recent voice calls (PSTN + WebRTC)
 * ────────────────────────────────────────────────
 * Shows the last 25 voice-call attempts with their leg statuses and any
 * Twilio ErrorCode/ErrorMessage captured by /voice/status-callback.
 *
 * The number-one use case: when a seeker reports "I heard 'Sorry, an
 * application error has occurred' after the phone rang", come here, find
 * their call by phone-last-4 and timestamp, and the failure code +
 * Twilio's verbatim explanation will be right there.
 */
const ERROR_HINT = {
  "11200": "HTTP retrieval failure — Twilio couldn't fetch the TwiML URL (DNS/SSL/firewall). Verify PUBLIC_BASE_URL points to the public domain.",
  "11205": "HTTP connection failure — proxy timed out fetching the webhook. Check that the host doesn't rate-limit Twilio IPs.",
  "11206": "HTTP protocol violation — TwiML endpoint returned non-XML. Look at the response body.",
  "11210": "HTTP bad host name — the URL host is invalid/unresolvable.",
  "11750": "TwiML response was syntactically invalid XML.",
  "13201": "Dial failed — invalid conference attributes.",
  "13214": "Dial failed — to/from numbers invalid.",
  "21219": "Twilio Trial restriction — verify the destination number in the Twilio console.",
};

function StatusPill({ status }) {
  const colorMap = {
    completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
    "in-progress": "bg-amber-100 text-amber-800 border-amber-300",
    initiated: "bg-stone-100 text-stone-700 border-stone-300",
    failed: "bg-rose-100 text-rose-800 border-rose-300",
  };
  const c = colorMap[status] || "bg-stone-100 text-stone-700 border-stone-300";
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${c}`}>{status || "—"}</span>;
}

export default function AdminVoiceCallsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/voice/recent-calls", { params: { limit: 25 } });
      setCalls(data?.calls || []);
    } catch {
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading || !user?.is_super_admin) return;
    reload();
  }, [authLoading, user]);

  if (authLoading) {
    return <PageShell><div className="max-w-5xl mx-auto py-12 px-4 text-muted">Loading&hellip;</div></PageShell>;
  }

  return (
    <PageShell>
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-voice-calls-page">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Phone size={28} weight="duotone" className="text-emerald-600" />
            <h1 className="font-serif-display text-3xl sm:text-4xl font-light">Recent Voice Calls</h1>
          </div>
          <button
            type="button"
            onClick={reload}
            className="btn-ghost rounded-full px-3 py-2 text-xs flex items-center gap-1.5"
            data-testid="voice-calls-reload-btn"
          >
            <ArrowsClockwise size={14} /> Reload
          </button>
        </div>
        <p className="text-sm text-muted mb-6">
          Last 25 attempts with Twilio leg-statuses + the exact <code>ErrorCode</code>
          and <code>ErrorMessage</code> captured from the status callback. Use
          this to diagnose &ldquo;Sorry, an application error has occurred&rdquo;
          audio errors heard by seekers.
        </p>

        {loading ? (
          <div className="text-muted text-sm">Loading&hellip;</div>
        ) : calls.length === 0 ? (
          <div className="text-muted text-sm" data-testid="voice-calls-empty">No calls in the last window.</div>
        ) : (
          <div className="space-y-3" data-testid="voice-calls-list">
            {calls.map((c) => {
              const failed = c.status === "failed" || c.last_error_code;
              return (
                <div
                  key={c.call_id}
                  className={`p-4 rounded-lg border ${failed ? "border-rose-300 bg-rose-50/40" : "border-stone-200 bg-white"}`}
                  data-testid={`voice-call-${c.call_id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      {failed ? (
                        <Warning size={16} weight="fill" className="text-rose-600" />
                      ) : (
                        <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                      )}
                      <span className="font-mono text-[11px] text-stone-600">{c.call_id}</span>
                      <StatusPill status={c.status} />
                      <span className="text-[10px] uppercase tracking-wider text-muted">{c.mode}</span>
                    </div>
                    <div className="text-[11px] text-muted">
                      {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs mb-2">
                    <div>
                      <span className="text-muted">Astrologer:</span>{" "}
                      <span className="font-semibold">{c.astrologer_name || c.astrologer_id || "—"}</span>
                    </div>
                    <div>
                      <span className="text-muted">Seeker:</span>{" "}
                      <span className="font-mono">****{c.seeker_phone_last4 || "----"}</span>
                    </div>
                    <div>
                      <span className="text-muted">Billed:</span>{" "}
                      <span className="font-mono">{c.billed_seconds ? `${c.billed_seconds}s` : "—"}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                    <div className="px-2 py-1 rounded bg-stone-100">
                      Seeker leg: <StatusPill status={c.leg_seeker_status} />
                      {c.leg_seeker_answered_by ? <span className="ml-1 text-muted">({c.leg_seeker_answered_by})</span> : null}
                    </div>
                    <div className="px-2 py-1 rounded bg-stone-100">
                      Astrologer leg: <StatusPill status={c.leg_astrologer_status} />
                      {c.leg_astrologer_answered_by ? <span className="ml-1 text-muted">({c.leg_astrologer_answered_by})</span> : null}
                    </div>
                  </div>

                  {failed && (c.last_error_code || c.leg_seeker_error_code || c.leg_astrologer_error_code) && (
                    <div className="mt-2 px-3 py-2 rounded-md bg-rose-100 border border-rose-300 text-xs">
                      <div className="font-semibold text-rose-900 mb-1">
                        Twilio ErrorCode {c.last_error_code || c.leg_seeker_error_code || c.leg_astrologer_error_code}
                        {c.last_error_leg && <span className="ml-2 text-[10px] text-rose-700">(on {c.last_error_leg} leg)</span>}
                      </div>
                      <div className="text-rose-800 mb-1 font-mono">
                        {c.last_error_message || c.leg_seeker_error_message || c.leg_astrologer_error_message || "—"}
                      </div>
                      {ERROR_HINT[c.last_error_code || c.leg_seeker_error_code || c.leg_astrologer_error_code] && (
                        <div className="text-[11px] text-rose-700 italic">
                          {ERROR_HINT[c.last_error_code || c.leg_seeker_error_code || c.leg_astrologer_error_code]}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </PageShell>
  );
}
